import React from 'react'
import { GetItemPropsOptions } from 'downshift'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { Item } from './item'

export type MenuItemRenderer<T> = (
  props: MenuItemRenderProps<T>
) => React.ReactElement<any>

interface MenuItemRenderProps<T> {
  readonly item: T
  readonly isSelected: boolean
  readonly isActive: boolean
  readonly isDisabled: boolean
}

export interface MenuProps<T> {
  readonly items: T[]
  readonly selectedItem?: T
  readonly itemToString: (item: T) => string
  readonly itemToKey: (item: T) => string
  readonly itemToGroup?: (item: T) => string
  readonly isItemDisabled?: (item: T) => boolean
  readonly highlightedIndex: number | undefined
  readonly getItemProps: (options: GetItemPropsOptions<T>) => any
  readonly menuItemRenderer?: MenuItemRenderer<T>
  readonly menuCss?: string
  readonly menuItemContainerCss?: string
  readonly menuItemActiveContainerCss?: string
  readonly menuItemTextCss?: string
  readonly menuItemSelectedTextCss?: string
  readonly menuFooterRenderer?: () => React.ReactElement<any>
  readonly e2eTestId?: string
}

interface IsSelectedProps<T> {
  readonly itemToKey: (item: T) => string
  readonly selectedItem: T
  readonly item: T
}

interface IsActiveProps {
  readonly highlightedIndex: number | undefined
  readonly index: number
  readonly isSelected: boolean
}

function isItemSelected<T>({
  itemToKey,
  selectedItem,
  item
}: IsSelectedProps<T>) {
  return itemToKey(selectedItem) === itemToKey(item)
}

function isItemActive({ highlightedIndex, index, isSelected }: IsActiveProps) {
  return (
    highlightedIndex === index ||
    (typeof highlightedIndex !== 'number' && isSelected)
  )
}

const defaultContainerCss = css`
  position: absolute;
  z-index: 1;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px 0;
  border-radius: 1px;
  border-width: 0 1px 1px;
  border-style: solid;
  background-color: ${theme.color.white};
  border-color: ${theme.color.neutral40};
`

const horizontalRuleCss = css`
  margin: 0;
  border: 0;
  margin-top: 5px;
  padding-bottom: 5px;
  border-top: 1px solid ${theme.color.neutral40};
`

function Divider(props) {
  return (
    <hr
      className={cx('legacy-ds-dropdown-menu-item-divider', horizontalRuleCss)}
      {...props}
    />
  )
}

const footerContainerCss = css`
  border-top: 1px solid ${theme.color.neutral30};
  background-color: ${theme.color.neutral10};
  padding: 8px;
  margin: 4px 0 -4px 0;
`

export function Menu<T>({
  items,
  selectedItem,
  itemToString,
  itemToKey,
  itemToGroup,
  isItemDisabled,
  highlightedIndex,
  getItemProps,
  menuItemRenderer,
  menuCss,
  menuItemContainerCss,
  menuItemActiveContainerCss,
  menuItemTextCss,
  menuItemSelectedTextCss,
  menuFooterRenderer,
  e2eTestId
}: MenuProps<T>) {
  function itemRenderer(item, index) {
    const isSelected =
      selectedItem !== undefined &&
      isItemSelected({ itemToKey, selectedItem, item })
    const isActive = isItemActive({
      highlightedIndex,
      index,
      isSelected
    })
    const isDisabled = !!isItemDisabled && isItemDisabled(item)
    return (
      <Item
        {...getItemProps({
          item,
          isSelected
        })}
        isActive={isActive}
        isDisabled={isDisabled}
        itemContainerCss={menuItemContainerCss}
        itemActiveContainerCss={menuItemActiveContainerCss}
        itemTextCss={menuItemTextCss}
        itemSelectedTextCss={menuItemSelectedTextCss}
        key={itemToKey(item)}
        data-e2e={`option-${itemToKey(item).toLowerCase()}`}
        data-testid={`dropdown-option-index-${index}`}
      >
        {menuItemRenderer
          ? menuItemRenderer({ item, isSelected, isActive, isDisabled })
          : itemToString(item)}
      </Item>
    )
  }

  let rendererItems: JSX.Element[] = []
  if (itemToGroup) {
    const groupedItems = items.reduce((groups, currentItem) => {
      const groupName = itemToGroup(currentItem)

      const group = groups[groupName]
        ? [...groups[groupName], currentItem]
        : [currentItem]

      return {
        ...groups,
        [groupName]: group
      }
    }, {})

    const totalGroups = Object.keys(groupedItems).length
    let dividers = 1
    let index = 0
    for (const group in groupedItems) {
      if (groupedItems.hasOwnProperty(group)) {
        rendererItems = rendererItems.concat(
          groupedItems[group].map((item) => itemRenderer(item, index++))
        )

        if (dividers < totalGroups) {
          rendererItems.push(<Divider key={dividers++} />)
        }
      }
    }
  } else {
    rendererItems = items.map(itemRenderer)
  }

  return (
    <div
      className={cx('legacy-ds-dropdown-menu', defaultContainerCss, menuCss)}
      data-e2e={e2eTestId}
    >
      {rendererItems}
      {!!menuFooterRenderer && (
        <div className={footerContainerCss}>{menuFooterRenderer()}</div>
      )}
    </div>
  )
}
